import React, { useContext, useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import Modal from "@components/modal";
import ImageContainer from "@components/imageContainer";
import { resolvePath } from "@utils/resolvePath";
import { Context } from "@components/useContext/context";
import {
  getWidgetSpecificIsVisible,
  getWidgetSpecificProps
} from "@utils/index";
import { CommonProps } from "@utils/interface";

import {
  ContentWrapper,
  TopImageWrapper,
  Title,
  SectionTitle,
  CardRow,
  Card,
  CardText,
  DotsWrapper,
  Dot,
  PrimaryButton,
  FeatureItem,
  FeatureTitle,
  FeatureDescription,
  FeatureList
} from "./styles";

interface MicroSDCard {
  imagePath?: string;
  imageAlt?: string;
  title: string;
  orderText?: string;
}

const mapStateToProps = (state: any, ownProps: any) => ({
  is_visible: getWidgetSpecificIsVisible(
    state.configuration?.normalisedConfig,
    ownProps.id
  ),
  ...getWidgetSpecificProps(state.configuration?.normalisedConfig, ownProps.id)
});

interface features {
  title: string;
  description: string;
}
interface MicroSDModalProps extends CommonProps {
  title?: string;
  topImageSrc?: string;
  topImageAlt?: string;
  howToChooseTitle?: string;
  requiredFeatures?: features[];
  recommendedCardsTitle?: string;
  recommendedCards?: MicroSDCard[];
  buttonText?: string;
}

const MicroSDModal = (props: MicroSDModalProps) => {
  const {
    is_visible,
    actions,
    title,
    topImageSrc,
    topImageAlt,
    howToChooseTitle,
    requiredFeatures,
    recommendedCardsTitle,
    recommendedCards,
    buttonText
  } = props;
  const { isDesktop } = useContext(Context);
  const { on_close_modal, on_click } = actions || {};
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const cardRowRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    on_close_modal?.forEach((eachFunc: any) => {
      typeof eachFunc.funCall === "function" && eachFunc.funCall(eachFunc.args);
    });
  };

  const handleButtonClick = () => {
    on_click?.forEach((eachFunc: any) => {
      typeof eachFunc.funCall === "function" && eachFunc.funCall(eachFunc.args);
    });
  };

  useEffect(() => {
    setActiveCardIndex(0); 
  }, [recommendedCards]);

  useEffect(() => {
    const cardRow = cardRowRef.current;
    if (!cardRow || !recommendedCards || recommendedCards.length === 0) return;

    const cards = cardRow.children;
    if (cards.length === 0) return;

    const handleScroll = () => {
      const scrollLeft = cardRow.scrollLeft;
      const containerWidth = cardRow.clientWidth;
      
      let maxVisible = 0;
      let activeIndex = 0;
      
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i] as HTMLElement;
        const cardLeft = card.offsetLeft;
        const cardRight = cardLeft + card.offsetWidth;
        const visibleLeft = Math.max(0, scrollLeft);
        const visibleRight = scrollLeft + containerWidth;
        const visibleStart = Math.max(visibleLeft, cardLeft);
        const visibleEnd = Math.min(visibleRight, cardRight);
        const visibleWidth = Math.max(0, visibleEnd - visibleStart);
        
        if (visibleWidth > maxVisible) {
          maxVisible = visibleWidth;
          activeIndex = i;
        }
      }
      
      setActiveCardIndex(activeIndex);
    };

    cardRow.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      cardRow.removeEventListener("scroll", handleScroll);
    };
  }, [recommendedCards]);

  return (
    <Modal
      showModal={is_visible}
      onClose={handleClose}
      showModalFooter={false}
      hideCloseButton={true}
      actions={actions}
      wrapperStyles={{ padding: "0" }}
      styles={{ padding: "0" }}
      lockBackgroundInteraction={props.lockBackgroundInteraction}
    >
      {topImageSrc && (
        <TopImageWrapper>
          <img
            src={resolvePath(topImageSrc || "")}
            alt={topImageAlt || ""}
            width={504}
            height={280}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
              display: "block"
            }}
          />
        </TopImageWrapper>
      )}

      <ContentWrapper isDesktop={isDesktop}>
        {title && <Title>{title}</Title>}

        {howToChooseTitle && <SectionTitle>{howToChooseTitle}</SectionTitle>}
        <FeatureList>
        {
          requiredFeatures &&
          requiredFeatures.map((feature, index) => (
            <FeatureItem key={index}>
              <FeatureTitle>{feature.title}: </FeatureTitle>
              <FeatureDescription> {feature.description}</FeatureDescription>
            </FeatureItem>
          ))
        }
        </FeatureList>
        {recommendedCardsTitle && (
          <SectionTitle>{recommendedCardsTitle}</SectionTitle>
        )}

        {recommendedCards && recommendedCards.length > 0 && (
          <>
            <CardRow ref={cardRowRef}>
              {recommendedCards.map((card, index) => (
                <Card key={index}>
                  <img
                    src={resolvePath(card.imagePath || "")}
                    alt={card.imageAlt}
                    width={48}
                    height={32}
                  />
                  <CardText>
                    {card.title}
                    <span>{card.orderText}</span>
                  </CardText>
                </Card>
              ))}
            </CardRow>

            <DotsWrapper>
              {recommendedCards.map((_, index) => (
                <Dot key={index} active={index === activeCardIndex} />
              ))}
            </DotsWrapper>
          </>
        )}

        {buttonText && (
          <PrimaryButton onClick={handleButtonClick}>{buttonText}</PrimaryButton>
        )}
      </ContentWrapper>
    </Modal>
  );
};

export default connect(mapStateToProps)(MicroSDModal);
